/* eslint-disable react/prop-types */
import { AnchorHTMLAttributes, forwardRef } from 'react';
import BrushStrokeWord, { BrushVariants, variants as brushVariants } from './BrushStrokeWord';
import CircledWord, { CircleVariants, variants as circleVariants } from './CircledWord';
import SharpieUnderlineWord, {
  UnderlineVariants,
  variants as underlineVariants,
} from './SharpieUnderlineWord';
import Typography, {
  DEPRECATED_VARIANTS,
  TypographyVariants,
  variants as typographyVariants,
} from './Typography';

export type ScribbleVariants =
  | TypographyVariants
  | UnderlineVariants
  | BrushVariants
  | CircleVariants;

type Props = AnchorHTMLAttributes<HTMLElement> & {
  variant?: ScribbleVariants;
  tag?: string;
  value?: string;
  svgName?: string;
  underlineColor?: string;
};

export const variants = [
  ...Object.keys(typographyVariants),
  ...Object.keys(underlineVariants),
  ...Object.keys(brushVariants),
  ...Object.keys(circleVariants),
] as ScribbleVariants[];

export const deprecatedVariants = [...DEPRECATED_VARIANTS];

const Scribble = forwardRef<React.ReactNode, Props>(({ variant = 'body', ...rest }, ref) => {
  if (isTypographyVariant(variant)) {
    return <Typography useBuiltInMargins variant={variant} {...rest} ref={ref} />;
  }
  if (isUnderlineVariant(variant)) {
    return <SharpieUnderlineWord variant={variant} {...rest} />;
  }
  if (isBrushVariant(variant)) {
    return <BrushStrokeWord variant={variant} {...rest} />;
  }
  if (isCircleVariant(variant)) {
    return <CircledWord variant={variant} circleColor={rest.color} {...rest} />;
  }

  return null;
});

Scribble.displayName = 'Scribble';

const isTypographyVariant = (variant: ScribbleVariants): variant is TypographyVariants =>
  !!typographyVariants[variant as unknown as TypographyVariants];

const isUnderlineVariant = (variant: ScribbleVariants): variant is UnderlineVariants =>
  !!underlineVariants[variant as unknown as UnderlineVariants];

const isBrushVariant = (variant: ScribbleVariants): variant is BrushVariants =>
  !!brushVariants[variant as unknown as BrushVariants];

const isCircleVariant = (variant: ScribbleVariants): variant is CircleVariants =>
  !!circleVariants[variant as unknown as CircleVariants];

export default Scribble;
